import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GomakePrimaryButton } from "@/components";
import {
  DieMaterialItem,
  directionTranslationKey,
  readDieSpecs,
} from "./gallery-die-display";
import { GalleryImageHover } from "./gallery-image-hover";
import { useStyle } from "./style";

const GalleryDieDetails = ({
  item,
  onSelect,
}: {
  item: DieMaterialItem;
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const { clasess } = useStyle();
  const specs = readDieSpecs(item);
  const [imageFailed, setImageFailed] = useState(false);
  const directionKey = directionTranslationKey(specs.direction);
  const direction = directionKey
    ? t(`materials.tableHeaders.${directionKey}`)
    : specs.direction;
  const titleName = specs.name || specs.sku || "";

  useEffect(() => {
    setImageFailed(false);
  }, [specs.imageSrc]);

  const rows = [
    { label: t("materials.tableHeaders.sku"), value: specs.sku },
    { label: t("products.galleryModal.subcategory"), value: specs.subcategory },
    { label: t("products.galleryModal.shape"), value: specs.shape },
    { label: t("products.galleryModal.width"), value: specs.width },
    { label: t("products.galleryModal.length"), value: specs.length },
    { label: t("products.galleryModal.dieSize"), value: specs.unitSize ? specs.dieSize : null },
    { label: t("products.galleryModal.dieUnitSize"), value: specs.dieUnitSize },
    { label: t("products.galleryModal.finalUnitSize"), value: specs.finalUnitSize },
    { label: t("products.galleryModal.columns"), value: specs.columns },
    { label: t("products.galleryModal.lines"), value: specs.lines },
    { label: t("products.galleryModal.columnSpacing"), value: specs.columnSpacing },
    { label: t("products.galleryModal.lineSpacing"), value: specs.lineSpacing },
    { label: t("products.galleryModal.direction"), value: direction },
    { label: t("products.galleryModal.type"), value: specs.type },
    { label: t("products.galleryModal.price"), value: specs.price },
    specs.active === null
      ? null
      : {
          label: t("products.galleryModal.status"),
          value: specs.active
            ? t("materials.tableHeaders.active")
            : t("products.galleryModal.inactive"),
        },
    specs.outsourced === null
      ? null
      : {
          label: t("products.galleryModal.outsourced"),
          value: specs.outsourced ? t("modal.yes") : t("modal.no"),
        },
  ].filter((row): row is { label: string; value: string } => Boolean(row?.value));

  return (
    <aside
      id="gallery-die-details"
      style={clasess.detailsPanel}
      aria-label={t("products.galleryModal.specifications", { name: titleName })}
    >
      <div style={clasess.detailsHeader}>
        <div style={clasess.detailsTitle}>
          {t("products.galleryModal.specifications", { name: titleName })}
        </div>
        {specs.active !== null && (
          <span
            style={
              specs.active ? clasess.statusBadgeActive : clasess.statusBadgeInactive
            }
          >
            {specs.active
              ? t("materials.tableHeaders.active")
              : t("products.galleryModal.inactive")}
          </span>
        )}
      </div>
      <div style={clasess.detailsImageWrap}>
        {specs.imageSrc && !imageFailed ? (
          <GalleryImageHover src={specs.imageSrc} alt={titleName}>
            <img
              src={specs.imageSrc}
              alt=""
              style={clasess.detailsImage}
              onError={() => setImageFailed(true)}
            />
          </GalleryImageHover>
        ) : (
          <div style={clasess.imagePlaceholder}>{t("products.galleryModal.noImage")}</div>
        )}
      </div>
      <dl style={clasess.detailsList}>
        {rows.map((row) => (
          <div key={row.label} style={clasess.detailsRow}>
            <dt style={clasess.detailsLabel}>{row.label}</dt>
            <dd style={clasess.detailsValue}>{row.value}</dd>
          </div>
        ))}
      </dl>
      <GomakePrimaryButton style={clasess.detailsSelectButton} onClick={onSelect}>
        {t("products.galleryModal.selectDie")}
      </GomakePrimaryButton>
    </aside>
  );
};

export { GalleryDieDetails };
